import type { BasePriceItem } from 'api-price'
import { observer } from 'mobx-react-lite'
import { FC, useState } from 'react'
import { ActionStyled, ActionTargetStyled, OverflowHideStyled } from './style'
import BaseHoverCard from '@/components/BaseHoverCard'
import ActionItem from './ActionItem'
import { useTranslation } from 'next-i18next'
import { eventTracker } from '@/utils/collectEvent'
import { fomartGateId, stopEvent, convertToKebabCase } from '@/utils/tools'
import { useAdapter } from '@/components/Adapter'
import { Flex } from '@mantine/core'
import { Button, openConfirmModal } from '@gate/gui'
import { useStore } from '@/store/index'
import { HOME_PAGE_URL } from '@/constants/index'
import useLangPrefix from '@/hooks/useLangPrefix'
import { BaseDrawerStyled } from '@/components/BaseDrawer/style'
import useRateChange from '@/hooks/useRateChange'
import BaseTooltip from '@/components/BaseTooltip'
import BaseImage from '@/components/BaseImage'
import { useRouter } from 'next/router'
import useCompliance from '@/hooks/useCompliance'

const Action: FC<BasePriceItem & { isFundraising?: boolean; isH5Fundraising?: boolean }> = ({
  isFundraising,
  gate_id,
  trade = [],
  detail = [],
  seo_path,
  crypto,
  fiat_name,
  is_gate,
}) => {
  const { locale } = useRouter()
  const { t } = useTranslation()
  const { isH5 } = useAdapter()
  const { langPrefix } = useLangPrefix()
  const { faitRateItem } = useRateChange()
  const { priceTab } = useStore().baseStore
  const [opened, setOpened] = useState(false)
  const { fiatTrate } = useCompliance()

  const onFundraisingModal = () => {
    openConfirmModal({
      styles: {
        header: {
          marginBottom: '24px',
        },
        body: {
          '.mantine-Group-root': {
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            gap: '24px',
          },
          '.mantine-Button-root': {
            marginTop: '16px',
            borderRadius: '18px',
          },
          '.mantine-Group-root .mantine-UnstyledButton-root:first-of-type': {
            display: 'none',
          },
          '.mantine-Group-root .mantine-UnstyledButton-root:nth-of-type(2)': {
            width: '100%',
          },
        },
      },
      radius: 8,
      padding: 24,
      centered: true,
      children: (
        <OverflowHideStyled gap={12} align="center" wrap={'wrap'} dir={locale === 'ar' ? 'rtl' : undefined}>
          {trade?.map((item: any) => (
            <Flex key={item?.link} align="center" gap={8}>
              <BaseImage
                style={{
                  borderRadius: '50%',
                  boxSizing: 'border-box',
                  border: '1px solid var(--color-card-3)',
                }}
                isRadius
                width={20}
                height={20}
                alt={item?.key}
                src={item?.imgUrl}
                loading="eager"
              />

              <span className="text-1 size-16 font-500">
                {item?.key}
                {item?.isLt === 1 ? '*' : ''}
              </span>
            </Flex>
          ))}
        </OverflowHideStyled>
      ),
      title: <span className="text-1 size-24 font-500">{t('p.investors', { ns: 'fundraising' })}</span>,
      labels: { cancel: false, confirm: `${t('p.got.it', { ns: 'fundraising' })}` },
    })
  }

  if (isFundraising) {
    return (
      <Flex
        onClick={e => {
          stopEvent(e)
          if (trade?.slice(2)?.length) {
            onFundraisingModal()
          }
        }}
        w={'max-content'}
        pl={5}
        align="center"
        gap={8}
        justify={'flex-start'}
        miw={'60px'}
        maw={'60px'}
      >
        <Flex align="center" gap={0}>
          {trade?.length ? '' : '--'}
          {trade?.slice(0, 2)?.map((item: any, index) => (
            <BaseTooltip
              style={{
                overflow: 'visible',
                zIndex: index + 1,
              }}
              zIndex={index + 1}
              isToolTipState
              key={item?.link}
              label={item?.key + (item?.isLt === 1 ? '*' : '')}
              className="text-3 size-14 font-400 line-120 currency-price"
            >
              <BaseImage
                style={{
                  marginLeft: '-5px',
                  zIndex: index + 1,
                  borderRadius: '50%',
                  boxSizing: 'border-box',
                  border: '1px solid var(--color-card-3)',
                }}
                isRadius
                width={20}
                height={20}
                alt={item?.key}
                src={item?.imgUrl}
                loading="eager"
              />
            </BaseTooltip>
          ))}
        </Flex>

        {trade?.slice(2)?.length ? (
          <span
            onClick={e => {
              stopEvent(e)
              onFundraisingModal()
            }}
            // onMouseEnter={onFundraisingModal}
            className="text-1 size-14 font-14"
          >
            +{trade?.slice(2)?.length}
          </span>
        ) : (
          ''
        )}
      </Flex>
    )
  }

  if (priceTab === HOME_PAGE_URL.FIAT) {
    return (
      <ActionStyled>
        <a
          href={`${langPrefix}/p2p/buy/${crypto?.toUpperCase()}-${fiat_name?.toUpperCase()}`}
          onClick={e => {
            stopEvent(e)
            location.href = `${langPrefix}/p2p/buy/${crypto?.toUpperCase()}-${fiat_name?.toUpperCase()}`
          }}
          className="text-5 font-500 size-14"
        >
          {t('P.Trade')}
        </a>

        <a
          href={`${langPrefix}/converter/${fomartGateId(
            gate_id,
            crypto
          )}/${crypto?.toLocaleLowerCase()}-to-${fiat_name?.toLocaleLowerCase()}`}
          onClick={e => {
            stopEvent(e)
            location.href = `${langPrefix}/converter/${fomartGateId(
              gate_id,
              crypto
            )}/${crypto?.toLocaleLowerCase()}-to-${fiat_name?.toLocaleLowerCase()}`
          }}
          className="text-5 font-500 size-14"
        >
          {t('P.Details')}
        </a>
      </ActionStyled>
    )
  }

  // 添加汇率换算入口
  const newDetailList = [
    ...detail,
    ...(fiatTrate
      ? []
      : [
          {
            key: 'convert-to',
            link: `/converter/${fomartGateId(gate_id, crypto)}/${crypto?.toLowerCase()}-to-usd`,
          },
        ]),
  ]

  const detailLabel = newDetailList?.map(item => {
    let currentLabel = ''

    if (item.key === 'how-to-buy') {
      currentLabel = t('P.HTB', {
        'Token abbreviation': crypto,
      })
    }

    if (item.key === 'price-prediction') {
      currentLabel = t('P.TAPP', {
        'Token abbreviation': crypto,
      })
    }

    if (item.key === 'convert-to') {
      currentLabel = t('P.Converter', { crypto })
    }

    return {
      value: item.key,
      link: item.link,
      label: currentLabel,
    }
  })

  if (isH5) {
    let h5ActionList = [
      {
        value: '1',
        link: `/price/${seo_path}`,
        label:
          t('P.TNAP', {
            'Token abbreviation': crypto,
          }) + '',
      },
      ...detailLabel,
    ]

    if (is_gate === '1000001' && trade?.length === 0) {
      h5ActionList = [
        ...h5ActionList,
        {
          value: 'buy-crypto',
          link: `${langPrefix}/crypto/buy/${gate_id}?fiat=${`${faitRateItem?.fiat ?? 'usd'}`.toLocaleLowerCase()}`,
          label: t('P.BuyCoin'),
        },
      ]
    }

    return (
      <Flex gap={8} justify="flex-end">
        <Button
          type="primary"
          size={'xs'}
          label={t('P.Trade') + ''}
          onClick={e => {
            stopEvent(e)
            setOpened(true)
          }}
        />
        <BaseDrawerStyled
          onClick={stopEvent}
          closeOnClickOutside
          position="bottom"
          styles={{
            header: {
              display: 'none',
            },
            title: {
              display: 'none',
            },
            closeButton: {
              display: 'none',
            },
            drawer: {
              boxSizing: 'border-box',
              padding: 0,
              minHeight: 'fit-content',
              height: 'fit-content',
              maxHeight: '80vh',
              borderTopLeftRadius: '12px',
              borderTopRightRadius: '12px',
            },
          }}
          title={''}
          withinPortal
          opened={opened}
          onClose={() => setOpened(false)}
        >
          <ActionItem
            handleClick={curItem => {
              actionTracker(curItem)
            }}
            list={trade
              ?.map(item => ({
                value: item?.pair,
                link: item?.link,
                label: item?.pair?.replace('_', '/'),
                tag: t(`P.${item.type?.replace(/^\w/, c => c.toUpperCase())}`) + '',
                type: item?.type,
              }))
              ?.concat(h5ActionList as any)}
          />
        </BaseDrawerStyled>
      </Flex>
    )
  }

  return (
    <ActionStyled>
      {!fiatTrate && is_gate === '1000001' && trade?.length === 0 && (
        <a
          href={`${langPrefix}/crypto/buy/${gate_id}?fiat=${`${faitRateItem?.fiat ?? 'usd'}`.toLocaleLowerCase()}`}
          onClick={e => {
            stopEvent(e)
            location.href = `${langPrefix}/crypto/buy/${gate_id}?fiat=${`${
              faitRateItem?.fiat ?? 'usd'
            }`.toLocaleLowerCase()}`
          }}
          className="text-5 font-500 size-14"
        >
          {t('P.BuyCoin')}
        </a>
      )}

      {!fiatTrate && trade?.length ? (
        <BaseHoverCard
          label={
            <ActionItem
              handleClick={curItem => {
                actionTracker(curItem)
              }}
              list={trade?.map(item => ({
                value: item?.pair,
                link: item?.link,
                label: item?.pair?.replace('_', '/'),
                tag: t(`P.${item?.type?.replace(/^\w/, c => c.toUpperCase())}`) + '',
                type: item?.type,
              }))}
            />
          }
        >
          <ActionTargetStyled onClick={stopEvent} className="text-5 font-500 size-14">
            {t('P.Trade')}
          </ActionTargetStyled>
        </BaseHoverCard>
      ) : null}

      <BaseHoverCard
        label={
          <ActionItem
            handleClick={curItem => {
              // 火山埋点
              actionTracker(curItem)
            }}
            list={[
              {
                value: '1',
                link: `/price/${seo_path}`,
                label:
                  t('P.TNAP', {
                    'Token abbreviation': crypto,
                  }) + '',
              },
            ].concat(detailLabel)}
          />
        }
      >
        <ActionTargetStyled onClick={stopEvent} className="text-5 font-500 size-14">
          {t('P.Details')}
        </ActionTargetStyled>
      </BaseHoverCard>
    </ActionStyled>
  )
}

/**
 * @description: 埋点封装
 * @param {Record} curItem
 * @param {*} any
 * @return {*}
 */
export const actionTracker = (curItem?: Record<string, any>) => {
  if (curItem?.type) {
    if (curItem?.type === 'spot') {
      // 现货
      eventTracker('price_treade_click', {
        button_name: 'spot',
      })
    }

    // 合约
    if (curItem?.type === 'perpetual') {
      eventTracker('price_treade_click', {
        button_name: 'futures',
      })
    }
  } else {
    if (curItem?.value === '1') {
      eventTracker('price_treade_click', {
        button_name: 'price',
      })
    }
    if (curItem?.value === 'how-to-buy') {
      eventTracker('price_treade_click', {
        button_name: 'how_buy',
      })
    }
    if (curItem?.value === 'price-prediction') {
      eventTracker('price_treade_click', {
        button_name: 'price_predict',
      })
    }
    if (curItem?.value === 'convert-to') {
      eventTracker('price_treade_click', {
        button_name: 'convert_to',
      })
    }
  }
}

export default observer(Action)
